import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { TodayVitals } from "@/components/today/TodayVitals";
import { RecoveryHeroCard } from "@/components/today/RecoveryHeroCard";
import { RecoveryTrendChart } from "@/components/health/RecoveryTrendChart";
import { EmptyState } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";
import { HRVTrendChart } from "@/components/health/HRVTrendChart";
import { SleepChart } from "@/components/health/SleepChart";
import { NutritionSleepChart } from "@/components/health/NutritionSleepChart";

/**
 * /health/recovery — sub-area home (SPEC 3.3.2). v1 stack:
 *  - Re-uses TodayVitals as the HRV/RHR/Sleep summary
 *  - Lists recent recovery_session entries (sauna / cold / mobility)
 *  - Links to subjective check-in log via Today's Quick-log strip
 *
 * Polished detail (HRV 90d trend chart, soreness body-map, streaks) lands
 * in BW9-10 once the recovery rules drive a richer ReadinessAssessment.
 */
export function HealthRecovery() {
  return (
    <article aria-labelledby="recovery-heading">
      <Eyebrow />
      <h1
        id="recovery-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-8)",
        }}
      >
        Recharge cycle.
      </h1>
      <div style={{ display: "grid", gap: "var(--space-6)" }}>
        <RecoveryHeroCard />
        <RecoveryTrendChart />
        <TodayVitals />
        <HRVTrendChart />
        <SleepChart />
        <NutritionSleepChart />
        <RecoverySessionList />
      </div>
    </article>
  );
}

function Eyebrow() {
  return (
    <p
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: "var(--type-micro)",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "var(--fg-3)",
        margin: 0,
      }}
    >
      <Link to="/health" style={{ color: "var(--fg-3)" }}>
        Health
      </Link>{" "}
      · Recovery
    </p>
  );
}

function RecoverySessionList() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const q = useQuery({
    queryKey: ["recovery_session_list", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const since = new Date();
      since.setDate(since.getDate() - 14);
      const { data, error } = await supabase
        .from("recovery_session")
        .select("id, ts, kind, duration_min, location, notes")
        .eq("user_id", userId)
        .gte("ts", since.toISOString())
        .order("ts", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-4)",
      }}
    >
      <h3 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--type-body)" }}>
        Recovery sessions · last 14 days
      </h3>
      {q.isLoading ? (
        <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>Loading…</p>
      ) : !q.data || q.data.length === 0 ? (
        <EmptyState
          headline="No recovery logged."
          sub="Sauna, cold plunge, mobility — log them from the Today quick-log strip."
        />
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
          {q.data.map((r) => (
            <li
              key={r.id}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: "var(--space-3)",
                alignItems: "baseline",
                fontSize: "var(--type-body-s)",
                color: "var(--fg-2)",
                padding: "6px 0",
                borderBottom: "1px solid var(--hair)",
              }}
            >
              <span className="mono" style={{ color: "var(--fg-3)" }}>
                {formatShortDate(r.ts)}
              </span>
              <span style={{ color: "var(--fg)" }}>{r.kind.replace(/_/g, " ")}</span>
              <span className="mono" style={{ color: "var(--fg-3)" }}>
                {r.duration_min ? `${r.duration_min}min` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
